import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import WebViewer, {Core, WebViewerInstance, WebViewerOptions} from '@pdftron/webviewer';
import {Observable, Subject} from 'rxjs';
import {UserService} from '../../../services/user.service';
import {distinctUntilChanged, filter, take, takeUntil} from 'rxjs/operators';
import {PdfViewerService} from '../../../services/pdf-viewer.service';
import {MessageService} from '../../../services/message-service.service';
import {FILE_SIZE_RESTRICTIONS} from '../../../models/upload-file-restrictions.model';
import {UtilsService} from '../../../services/utils.service';
import moment from 'moment';
import {TranslateService} from '@ngx-translate/core';
import {LoaderService} from '../../../services/loader.service';
import {UserTheme} from '../../../../shared/models/user.model';
import {UserSettingsService} from '../../../../shared/services/user-settings.service';
import {PdftronKeyService} from '../../../../po-approval-manager/services/pdf-config.service';
interface MenuItem {
  type: string;
  className?: string;
  img?: string;
  onClick?: () => void;
  dataElement?: string;
  label?: string;
}

@Component({
  selector: 'app-pdf-viewer',
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.scss']
})
export class PdfViewerComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly timeoutForIE = 50;
  private readonly env = '__env';
  private readonly restrictionOptions = FILE_SIZE_RESTRICTIONS.GLOBAL;
  private webViewerConfig: WebViewerOptions;

  @ViewChild('viewer', {static: false}) viewer: ElementRef;
  wvInstance: WebViewerInstance;
  @ViewChild('compareViewer', {static: false}) compareViewer: ElementRef;
  compareWvInstance: WebViewerInstance;
  compareMode = false;
  //Reference on update v11.2.0: https://docs.apryse.com/web/guides/web-component-vs-iframe
  WebViewerFunc = WebViewer.Iframe;
  currentVersionObs: Observable<any>;
  compareVersionObs: Observable<any>;
  downloadWithAnnotObs: Observable<any>;
  downloadAllWithAnnotObs: Observable<any>;
  currentVersion: any;
  compareVersion: any;
  initialized = false;
  private originalScroller: any;
  private scrollTimeout: any;
  private isDownloadAllInProgress: boolean;
  private ngUnsubscribe = new Subject<void>();
  private showReadOnlyViewer = false;

  constructor(private userService: UserService,
              private pdfViewerService: PdfViewerService,
              private messageService: MessageService,
              private translateService: TranslateService,
              private loaderService: LoaderService,
              private cd: ChangeDetectorRef,
              private userSettingsService: UserSettingsService,
              private pdftronKeyService: PdftronKeyService) {
    this.currentVersionObs = this.pdfViewerService.selectedVersionChanged();
    this.compareVersionObs = this.pdfViewerService.compareVersionChanged();
    this.downloadWithAnnotObs = this.pdfViewerService.downloadClicked();
    this.downloadAllWithAnnotObs = this.pdfViewerService.getDownloadAllSubject();
  }

  async ngOnInit(): Promise<void> {
    this.pdftronKeyService.licenseKey$
      .pipe(
        filter(key => !!key),
        take(1)
      )
      .subscribe(key => {
        this.webViewerConfig = {
          path: '../webviewer',
          fullAPI: true,
          licenseKey: key ?? '',
          enableAzureWorkaround: true,
          disabledElements: ['toolbarGroup-Edit'],
        };
      });

    this.pdfViewerService.compareModeChanged()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => this.compareMode = value);
  }

  ngAfterViewInit() {
    this.pdfViewerService.initializedChanged()
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe((value) => {
      if (value.initialize !== this.initialized && value.initialize === true) {
        this.initialized = true;
        this.showReadOnlyViewer = value.disableAnnotations;
        this.cd.detectChanges();
        setTimeout(() => this.initializeViewers());
      }
    });
  }

  initializeViewers(): void {
    Promise.all([this.initMainViewer(), this.initCompareViewer()])
    .then(async () => {
      const vwInstances = [this.wvInstance, this.compareWvInstance];
      const viewers = [this.wvInstance.Core.documentViewer, this.compareWvInstance.Core.documentViewer];
      const docContainers = [
        this.viewer.nativeElement.querySelector('iframe').contentDocument.querySelector('.DocumentContainer'),
        this.compareViewer.nativeElement.querySelector('iframe').contentDocument.querySelector('.DocumentContainer')
      ];
      this.syncZoom(viewers, vwInstances);
      this.syncRotation(viewers);
      this.addScrollListener(docContainers);
      this.disableTools(vwInstances);
      this.initClips(vwInstances);
      this.initToolListeners(vwInstances);
      this.currentVersionObs
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((version) => {
        this.currentVersion = version;
        this.loadDoc(this.wvInstance, this.currentVersion);
        this.cd.detectChanges();
      });
      this.compareVersionObs
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((version) => {
        this.compareVersion = version;
        this.loadDoc(this.compareWvInstance, this.compareVersion);
        this.cd.detectChanges();
      });
      this.downloadWithAnnotObs
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((filename) => {
        this.downloadWithAnnotations(filename);
      });
      this.downloadAllWithAnnotObs
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((versions) => {
          this.downloadAllWithAnnotations(versions);
        });
    });
  }

  private initMainViewer(): Promise<any> {
    return this.WebViewerFunc(this.webViewerConfig, this.viewer.nativeElement)
    .then(instance => {
        this.wvInstance = instance;
        this.userSettingsService.userTheme$.pipe(
          distinctUntilChanged(),
          takeUntil(this.ngUnsubscribe)
        ).subscribe((userTheme) => {
          if (userTheme === UserTheme.DARK) {
            instance.UI.setTheme('dark');
          } else {
            instance.UI.setTheme('light');
          }
        });
        instance.Core.annotationManager.addEventListener('annotationChanged',
          async (annotations: Core.Annotations.Annotation[], action: string, {imported}) => {
            if (imported) {
              return;
            }
            const canAdd = await this.verifyAnnotationCanBeAdded(action, annotations, instance);
            if (canAdd) {
              this.updateAnnotations(instance.Core.annotationManager, this.currentVersion);
            }
          });

        instance.Core.documentViewer.addEventListener('documentLoaded', async () => {
          await this.initAnnotationUser(instance);
          this.loadAnnot(instance, instance.Core.annotationManager, this.currentVersion);
          instance.UI.setZoomLevel('100%');

          if (this.showReadOnlyViewer) { instance.Core.annotationManager.enableReadOnlyMode(); }
        });

      }
    );
  }

  private initCompareViewer(): Promise<any> {
    return this.WebViewerFunc(this.webViewerConfig, this.compareViewer.nativeElement)
    .then(instance => {
        this.compareWvInstance = instance;

        this.userSettingsService.userTheme$.pipe(
          distinctUntilChanged(),
          takeUntil(this.ngUnsubscribe)
        ).subscribe((userTheme) => {
          if (userTheme === UserTheme.DARK) {
            instance.UI.setTheme('dark');
          } else {
            instance.UI.setTheme('light');
          }
        });

        instance.Core.annotationManager.addEventListener('annotationChanged',
          async (annotations: Core.Annotations.Annotation[], action: string, {imported}) => {
            if (imported) {
              return;
            }
            const canAdd = await this.verifyAnnotationCanBeAdded(action, annotations, instance);
            if (canAdd) {
              this.updateAnnotations(instance.Core.annotationManager, this.compareVersion);
            }
          });

        instance.Core.documentViewer.addEventListener('documentLoaded', async () => {
          if (!this.isDownloadAllInProgress) {
            await this.initAnnotationUser(instance);

            this.loadAnnot(instance, instance.Core.annotationManager, this.compareVersion);
            instance.UI.setZoomLevel('100%');

            if (this.showReadOnlyViewer) { instance.Core.annotationManager.enableReadOnlyMode(); }
          }
        });
      }
    );
  }

  private async verifyAnnotationCanBeAdded(action: string, annotations: Core.Annotations.Annotation[], instance) {
    if (action === 'add') {
      const fileAnnotations = annotations
      .filter(annotation => annotation.Subject === 'File Attachment');
      const lobs = await Promise.all(fileAnnotations
      .map((annotation: Core.Annotations.FileAttachmentAnnotation) => annotation.getFileData()));
      if (lobs.some(lob => !UtilsService.isFileFitsBySize(lob.size, this.restrictionOptions))) {
        this.messageService.showSystemMessage(
          'error',
          this.translateService.instant('common.errors.file-size-error'),
          this.translateService.instant('common.errors.file-size-limit',
            {sizeNumber: this.restrictionOptions.sizeNumber, sizeType: this.restrictionOptions.sizeType}));
        instance.Core.annotationManager.deleteAnnotations(fileAnnotations, false, true, false);
        return false;
      }
    }
    return true;
  }

  public downloadWithAnnotations(filename?: string) {
    this.wvInstance.UI.downloadPdf({
      filename
    });
  }

  public async downloadAllWithAnnotations(versions): Promise<void> {
    const downloadFile = async (version) => {
      const loadedSubject = new Subject();

      const onDocumentLoaderCallback = async () => {
        try {
          this.compareWvInstance.Core.documentViewer.removeEventListener('documentLoaded', onDocumentLoaderCallback);

          const comments = await this.pdfViewerService.approvalManagerService.getComments(version).toPromise();
          await this.compareWvInstance.Core.annotationManager.importAnnotations(comments);

          this.compareWvInstance.UI.downloadPdf({
            filename: version.attachment?.fileName,
          });

          setTimeout(() => {
            loadedSubject.next(version);
            loadedSubject.complete();
          }, 1000);
        } catch (e) {
          console.error('Error importing annotations or downloading PDF:', e);
          loadedSubject.error(e);
        }
      };

      this.compareWvInstance.Core.documentViewer.addEventListener('documentLoaded', onDocumentLoaderCallback);
      await this.loadDoc(this.compareWvInstance, version);

      return loadedSubject.toPromise();
    };

    this.isDownloadAllInProgress = true;
    try {
      for (const version of versions) {
        await downloadFile(version);
      }
    } catch (e) {
      console.error('Error downloading files:', e);
    } finally {
      this.isDownloadAllInProgress = false;
    }
  }

  private async loadDoc(wvInstance: WebViewerInstance, version) {
    if (!version) {
      await this.closeDocument(wvInstance);
      return;
    }

    const downloadRequest = await this.pdfViewerService.approvalManagerService.getDownloadRequest(version);
    const extension = this.pdfViewerService.approvalManagerService.getVersionFileExtension(version);

    if (['tif', 'tiff'].indexOf((extension.toLowerCase() || ''.toLowerCase())) === -1) {
      wvInstance?.UI.loadDocument(downloadRequest.url, {
        extension,
        filename: version.attachment?.fileName,
        customHeaders: downloadRequest.customHeaders
      });
      return;
    }
    const PDFNet = wvInstance.Core.PDFNet;

    let newDoc;

    this.loaderService.showLoader();
    await PDFNet.runWithoutCleanup(() => PDFNet.PDFDoc.create()
      .then((doc) => {
        newDoc = doc;
        newDoc.initSecurityHandler();
        newDoc.lock();
        return PDFNet.Filter.createURLFilter(downloadRequest.url);
      })
      .then((tiffFile) => PDFNet.Convert.fromTiff(newDoc, tiffFile)).then(() => {
        newDoc.unlock();
        wvInstance.UI.loadDocument(newDoc, {
          customHeaders: downloadRequest.customHeaders
        });
      }), this.pdftronKeyService.getLicenseKey());

    this.loaderService.hideLoader();
  }


  private async initAnnotationUser(instance: WebViewerInstance) {
    const userDetails = this.userService.getUserDetails();
    const userName = userDetails ? `${userDetails.firstName} ${userDetails.lastName}` : 'Guest';
    const menuItem: MenuItem[] = [{
      type: 'actionButton',
      className: 'row',
      img: 'icon-header-print-line',
      onClick: () => {
        instance.UI.print();
      },
      dataElement: 'alertButton',
      label: 'Print'
    },
    {
      type: 'actionButton',
      className: 'row',
      img: 'icon-download',
      onClick: () => {
        instance.UI.downloadPdf();
      },
      dataElement: 'alertButton',
      label: 'Download'
    }, {
      type: 'actionButton',
      className: 'row',
      img: 'icon-header-full-screen',
      onClick: () => {
        instance.UI.toggleFullScreen();
      },
      dataElement: 'alertButton',
      label: 'Full Screen'
    }];
    instance.Core.annotationManager.setCurrentUser(userName);
    /* await this.pdfViewerService.approvalManagerService.canAnnotate() ?
    instance.Core.annotationManager.enableReadOnlyMode() :
    instance.Core.annotationManager.disableReadOnlyMode(); */
    //instance.UI.disableElements(this.webViewerConfig.disabledElements);
    //instance.UI.settingsMenuOverlay.update(menuItem);
  }

  private async closeDocument(wvInstance: WebViewerInstance) {
    wvInstance?.Core.documentViewer.closeDocument();
    await wvInstance?.Core.documentViewer.closeDocument();
  }

  private updateAnnotations = async (annotManager, version) => {
    const xfdfString = await annotManager.exportAnnotations();
    this.pdfViewerService.approvalManagerService.saveComments(version, xfdfString)
    .subscribe(() => {
      console.log('comment saved!');
    });
  };

  private loadAnnot(instance: WebViewerInstance, annotManager: Core.AnnotationManager, version) {
    if (!version) {
      return;
    }

    return this.pdfViewerService.approvalManagerService.getComments(version)
    .subscribe((text: string) => {
      annotManager.importAnnotations(text)
      .then(() => {
        console.log('annotations updated');
      });
    }, () => {
      annotManager.importAnnotations('')
      .then(() => {
        console.log('annotations created');
      });
    }, () => {
      if (window.localStorage.getItem(this.getLocalStorageOpenNotesKey(instance)) === 'true') {
        instance.UI.openElements(['notesPanel']);
      }
    });
  }

  private getLocalStorageOpenNotesKey(instance: WebViewerInstance) {
    return instance.UI.iframeWindow.frameElement.id + '_open';
  }

  private syncZoom(viewers: Core.DocumentViewer[], vwInstances: WebViewerInstance[]) {
    viewers.forEach(viewer => {
      viewer.addEventListener('zoomUpdated', zoom => {
        vwInstances.forEach(instance => {
          if (instance.UI.getZoomLevel() !== zoom) {
            instance.UI.setZoomLevel(zoom);
          }
        });
      });
    });
  }

  private syncRotation(viewers: Core.DocumentViewer[]) {
    viewers.forEach(docViewer => {
      docViewer.addEventListener('rotationUpdated', rotation => {
        viewers.forEach(viewer => {
          if (viewer.getRotation() !== rotation) {
            viewer.setRotation(rotation);
          }
        });
      });
    });
  }

  private addScrollListener(docContainers) {
    docContainers.forEach(documentContainer => {
      documentContainer.onscroll = () => {
        if (!this.originalScroller || this.originalScroller === documentContainer) {
          this.originalScroller = documentContainer;
          this.syncScrolls(docContainers, documentContainer.scrollLeft, documentContainer.scrollTop);
          clearTimeout(this.scrollTimeout);
          this.scrollTimeout = setTimeout(() => {
            this.originalScroller = null;
          }, this.timeoutForIE);
        }
      };
    });
  }

  private syncScrolls(docContainers, scrollLeft, scrollTop) {
    docContainers.forEach(documentContainer => {

      if (documentContainer.scrollLeft !== scrollLeft) {
        documentContainer.scrollLeft = scrollLeft;
      }

      if (documentContainer.scrollTop !== scrollTop) {
        documentContainer.scrollTop = scrollTop;
      }
    });
  }

  private initFileClip(instance: WebViewerInstance) {
    instance.Core.documentViewer.addEventListener(
      'annotationAdded', async (annotation: Core.Annotations.FileAttachmentAnnotation) => {
      instance.Core.annotationManager.selectAnnotation(annotation);
      instance.UI.focusNote(annotation.Id);
    });

    function formatDate(annotation, dateAndTime) {
      if (annotation.DateModified) {
        dateAndTime.innerHTML = moment(annotation.DateModified.toUTCString())
        .format('MM/DD/YYYY, hh:mm a')
        .toUpperCase();
      } else {
        dateAndTime.innerHTML = '';
      }
    }

    instance.UI.dangerouslySetNoteTransformFunction((wrapper, state, createElement) => {
      // Change the title of every note
      const annotation = state.annotation;
      if (annotation.Subject.toLowerCase() === 'file attachment') {
        const div = createElement('div');
        div.href = '#';
        div.style = 'line-height: 16px;' +
          'display: flex;' +
          'padding-top: 8px;' +
          'align-items: center;';
        div.onclick = (e: MouseEvent) => {
          instance.Core.annotationManager.trigger('annotationDoubleClicked', state.annotation);
          e.preventDefault();
        };
        const fileMetadata = this.getMetadata(annotation);
        div.innerHTML = `<img src="/assets/icons/paperclip.svg" width="16px" height="16px"><span>${fileMetadata.filename}</span>`;
        wrapper.querySelector('.author-and-date')?.append(div);
      }

      // Transform the notes styles
      const noteContents = wrapper.querySelectorAll('.NoteContent .container');
      noteContents.forEach((element) => {
        (element as HTMLElement).style.pointerEvents = 'auto';
        (element as HTMLElement).style.userSelect = 'text';
      });

      const dateAndTime = wrapper.querySelector('.date-and-time') as HTMLElement | null;
      if (dateAndTime) { formatDate(annotation, dateAndTime); }

      if (annotation.getReplies() && annotation.getReplies().length) {
        annotation.getReplies().forEach((annotationReply, index) => {
          const replyDateContainer = wrapper.querySelector(`.replies .NoteContent:nth-child(${index + 1}) .date-and-time`);
          if (replyDateContainer) {
            formatDate(annotationReply, replyDateContainer);
          }
        });
      }
    });
  }

  private getMetadata(annotation: any) {
    return annotation.getFileMetadata();
  }

  private disableTools(viewers: Array<WebViewerInstance>) {
    viewers.forEach((viewer: WebViewerInstance) => {
      viewer.UI.disableElements(['thumbnailControl', 'documentControl', ...this.webViewerConfig.disabledElements]);
    });
  }

  private initClips(viewers: Array<WebViewerInstance>) {
    viewers.forEach((viewer: WebViewerInstance) => {
      this.initFileClip(viewer);
    });
  }

  private initToolListeners(viewers: Array<WebViewerInstance>) {
    viewers.forEach((viewer: WebViewerInstance, index) => {
      const notesButton = viewer.UI.iframeWindow.document.querySelector(`button[data-element='notesPanelToggle']`);
      notesButton?.addEventListener('click', () => {
        setTimeout(() => window.localStorage.setItem(this.getLocalStorageOpenNotesKey(viewer),
          `${viewer.UI.isElementOpen('notesPanel')}`), 500);
      });
    });
  }

  async ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    await this.disposeViewer(this.wvInstance);
    await this.disposeViewer(this.compareWvInstance);
  }

  private async disposeViewer(wvInstance: WebViewerInstance) {
    await this.closeDocument(wvInstance);
    wvInstance?.UI.dispose();
  }
}
